import HelpCenterLayout from "@components/helpCenter/HelpCenterLayout"
import {
  AccessUntil,
  ResultView,
  StoreLinks,
} from "@components/helpCenter/ResultParts"
import { resultTextComponents } from "@components/helpCenter/resultTextComponents"
import { decideCancelationResult } from "@helpers/helpCenterResult"
import { CancelationType } from "@pages/helpCenter/CancelationPage"
import { Trans, useTranslation } from "react-i18next"
import { Navigate, useLocation } from "react-router-dom"
import { CancelRevenueCatSubscriptionsData } from "src/types"

export type CancelationCompletedPageLocationState =
  Partial<CancelRevenueCatSubscriptionsData> & {
    /** ISO date the canceled web subscriptions' Pro access ends, if known. */
    accessEndDate?: string | null
    cancelationType: CancelationType
  }

export default function CancelationCompletedPage() {
  const { t } = useTranslation()
  const location = useLocation()

  const state = location.state as CancelationCompletedPageLocationState | null

  // NOTE: Opened directly (e.g. reloaded after the state was lost): there's
  // no outcome to report.
  if (!state?.cancelationType) return <Navigate replace to="/help-center" />

  const { accessEndDate, cancelationType } = state

  const { hasStoreSubscription, layout, storeInstructions, web } =
    decideCancelationResult(state, cancelationType)

  const webMessage = () => {
    switch (web) {
      case "canceled":
        return (
          <>
            <p>
              <Trans
                components={resultTextComponents}
                i18nKey={
                  hasStoreSubscription
                    ? "pages.helpCenter.cancelationCompleted.webSubscriptionCanceled"
                    : "pages.helpCenter.cancelationCompleted.subscriptionSuccessfullyCanceled"
                }
              />
            </p>

            <AccessUntil date={accessEndDate} />
          </>
        )

      case "notConfirmed":
        return (
          <p>
            <Trans
              components={resultTextComponents}
              i18nKey="pages.helpCenter.cancelationCompleted.cancelationNotConfirmed"
            />
          </p>
        )

      case "noWebSubscription":
        // NOTE: Next to store instructions, the user simply has no web
        // subscription: nothing to report about it.
        if (storeInstructions) return null

        return (
          <p>
            <Trans
              components={resultTextComponents}
              i18nKey="pages.helpCenter.cancelationCompleted.noWebSubscriptionToCancel"
            />
          </p>
        )
    }
  }

  if (!storeInstructions)
    return (
      <HelpCenterLayout>
        <ResultView
          icon={layout === "success" ? "success" : "warning"}
          title={
            layout === "success"
              ? t("pages.helpCenter.thankYou")
              : t("pages.helpCenter.weAreSorry")
          }
        >
          {webMessage()}
        </ResultView>
      </HelpCenterLayout>
    )

  const { apple, google } = storeInstructions

  const regardingSubscriptionTranslation = () => {
    if (apple && google)
      return t("pages.helpCenter.regardingGoogleAndAppleSubscriptions")

    if (google) return t("pages.helpCenter.regardingGoogleSubscription")

    return t("pages.helpCenter.regardingAppleSubscription")
  }

  const followGuidelinesTranslation = () => {
    if (apple && google)
      return t(
        "pages.helpCenter.cancelationCompleted.followGoogleAndAppleGuidelines",
      )

    if (apple)
      return t("pages.helpCenter.cancelationCompleted.followAppleGuidelines")

    return t("pages.helpCenter.cancelationCompleted.followGoogleGuidelines")
  }

  return (
    <HelpCenterLayout>
      <ResultView
        icon="warning"
        title={t("pages.helpCenter.shared.furtherActionNeeded")}
      >
        {webMessage()}

        <p className="font-semibold text-vm-ink">
          {regardingSubscriptionTranslation()}
        </p>

        <p>
          {followGuidelinesTranslation()}{" "}
          <span className="font-semibold text-vm-ink">
            {t(
              "pages.helpCenter.cancelationCompleted.followInstructionsToCancel",
            )}
          </span>
        </p>

        <StoreLinks apple={apple} google={google} />
      </ResultView>
    </HelpCenterLayout>
  )
}
